import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { RequiredItemsChecklistService } from '../../subaward/required-items-checklist.service';
import {
  UserProfilesClient,
  SubawardsClient,
  SubrecipientStatusVm,
} from 'src/app/api.service';
import { AuthService } from 'src/app/auth.service';
import { SubawardsService } from '../../subawards.service';
import { FeatureService } from '../../../utilities/feature.service';
import { SubStatusService } from '../../../utilities/subrecipient-status/sub-status.service';
import { DatePipe } from '@angular/common';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'subs-washu-information',
  templateUrl: './washu-information.component.html',
})
export class WashuInformationComponent implements OnInit, OnChanges {
  @Input() form: UntypedFormGroup;
  @Input() persistedStatus: string;
  @Input() showSubawardId: boolean;
  @Input() showComplianceChecks: boolean;
  @Input() showGenerateAgreementNumber: boolean;
  @Input() validatingGrantWorkTag: boolean;
  @Output() grantWorkTagEntered = new EventEmitter<string>();
  @Output() poCoRequestIdEntered = new EventEmitter<string>();

  assignedGAOptions: Array<string> = [];
  activeStatusList: SubrecipientStatusVm[] = [];
  isActiveFeatureGrantWorkTag = true;
  hasAccessToOsrsStatuses = false;
  showAgreementNumberModal = false;
  agreementNumberModalMode: 'generate' | 'manual' = 'generate';
  originalAgreementNumber = '';
  agreementNumberFocused = false;

  constructor(
    private checklistService: RequiredItemsChecklistService,
    private subawardsService: SubawardsService,
    private userProfileClient: UserProfilesClient,
    private subawardsClient: SubawardsClient,
    private authService: AuthService,
    private featureService: FeatureService,
    private subStatusService: SubStatusService,
  ) {}

  ngOnInit(): void {
    this.subStatusService.ActiveStatuses.subscribe((value) => {
      if (value !== undefined && value !== null) {
        this.activeStatusList = value;
        // apply the DA/OSRS permission rules to the reactive Status control
        this.applyStatusControlAccess();
      }
    });
    this.subawardsService.OsrsGaList.subscribe((value) => {
      const currentlyAssignedGA = this.form.controls.assignedGA.value;
      if (value) {
        value.forEach((u) => {
          this.assignedGAOptions = value;
          if (
            currentlyAssignedGA &&
            !this.assignedGAOptions.includes(currentlyAssignedGA)
          ) {
            this.assignedGAOptions.push(currentlyAssignedGA);
          }
        });
      }
    });
    this.hasAccessToOsrsStatuses =
      this.authService.hasPermission('AccessStatusOSRS');
    this.featureService
      .featureEnabled('WDGrantWorkTag')
      .subscribe((feature) => {
        if (feature && feature.featureName) {
          if (feature.featureName.toLowerCase() === 'wdgrantworktag') {
            this.isActiveFeatureGrantWorkTag = feature.featureEnabled;
          }
        }
      });
    this.form.controls.poCoEncumbId.valueChanges.subscribe((value) => {
      this.subawardsService.setPoCoEncumbId(value);
    });
    this.form.controls.poCoDocId.valueChanges.subscribe(() => {
      if (!!this.form.controls.poCoDocId.value) {
        this.form.controls.poCoDocId.patchValue(
          this.form.controls.poCoDocId.value.toUpperCase(),
          {
            emitEvent: false,
          },
        );
      }
    });

    this.form.controls.supplierContractId.valueChanges.subscribe((value) => {
      this.subawardsService.setSupplierContractId(value);
    });

    // Story 779 set status to "Opened by OSRS" when a GA is assigned and the status is "Submitted to OSRS"
    this.form.controls.assignedGA.valueChanges.subscribe((GA) => {
      this.form.controls.assignedGA.setValue(GA, {
        onlySelf: true,
        emitEvent: false,
      });
      const status = this.form.controls.status.value;
      if (status === 'SO' && GA !== '' && GA != null) {
        this.form.controls.status.setValue('OBO');
      }
    });

    // Seed the baseline immediately from whatever value the form already holds.
    // This handles the case where setSubawardForm ran before this component initialized.
    this.originalAgreementNumber =
      this.form.controls.agreementNumber.value ?? '';

    // Also update the baseline on the first valueChanges emission in case the form
    // data arrives after this component initializes (async API load).
    // Any subsequent emission while the field is not focused is a programmatic change.
    this.form.controls.agreementNumber.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((val) => {
        if (!this.originalAgreementNumber) {
          this.originalAgreementNumber = val ?? '';
          return;
        }
        if (
          !this.agreementNumberFocused &&
          (val ?? '') !== this.originalAgreementNumber
        ) {
          this.agreementNumberModalMode = 'manual';
          this.showAgreementNumberModal = true;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // persistedStatus can arrive/change after ngOnInit; parent loads the subaward asynchronously
    // reapply status access whenever 'persistedStatus' changes
    if (
      changes['persistedStatus'] &&
      this.form &&
      this.activeStatusList.length > 0
    ) {
      this.applyStatusControlAccess();
    }
  }

  setCheckboxDate(event, control: string) {
    const inputElement = event.target as HTMLInputElement;
    const isChecked = inputElement.checked;

    if (isChecked) {
      const datePipe = new DatePipe('en-US');
      const date = datePipe.transform(new Date(), 'MM/dd/yyyy');
      this.form.controls[control].patchValue(date, {
        emitEvent: false,
      });
    } else {
      this.form.controls[control].patchValue(null, {
        emitEvent: false,
      });
    }
  }

  generateAgreementNumber() {
    this.agreementNumberModalMode = 'generate';
    this.showAgreementNumberModal = true;
  }

  onAgreementNumberFocus() {
    this.agreementNumberFocused = true;
    this.originalAgreementNumber =
      this.form.controls.agreementNumber.value ?? '';
  }

  onAgreementNumberBlur() {
    this.agreementNumberFocused = false;
    const current = this.form.controls.agreementNumber.value ?? '';
    if (
      this.originalAgreementNumber !== '' &&
      current !== this.originalAgreementNumber
    ) {
      this.agreementNumberModalMode = 'manual';
      this.showAgreementNumberModal = true;
    }
  }

  confirmAgreementNumberChange() {
    this.showAgreementNumberModal = false;
    if (this.agreementNumberModalMode === 'generate') {
      this.subawardsClient.getSequence().subscribe((val) => {
        this.form.controls.agreementNumber.setValue(val, { emitEvent: false });
        this.originalAgreementNumber = val;
        if (this.form.controls.trackingNumber) {
          const trackingNumber = this.form.controls.trackingNumber.value;
          if (!trackingNumber || trackingNumber === '') {
            this.form.controls.trackingNumber.setValue(val);
          }
        }
      });
    } else {
      this.originalAgreementNumber =
        this.form.controls.agreementNumber.value ?? '';
    }
  }

  cancelAgreementNumberChange() {
    this.showAgreementNumberModal = false;
    if (this.agreementNumberModalMode === 'manual') {
      this.form.controls.agreementNumber.setValue(
        this.originalAgreementNumber,
        { emitEvent: false },
      );
    }
  }

  canGenerateNumber() {
    const status = this.form.controls.status.value;
    const allowedStatus = [
      'SRV',
      'SO',
      'RC',
      'RD',
      'OBO',
      'POC',
      'POA',
      'PT',
      'PA',
      'OR',
      'SMA',
      'IN',
      'STS',
      'SR',
      'RA',
      'SD',
      'OUO',
      'VD',
      'IA',
    ];
    return (
      this.authService.hasPermission('WidgetOsrsOnlyUpdate') &&
      allowedStatus.includes(status)
    );
  }

  get canOsrsUpdate(): boolean {
    return this.authService.hasPermission('WidgetOsrsOnlyUpdate');
  }

  viewOnlyUser(): boolean {
    return (
      this.authService.hasPermission('ReadOnlyGlobalAccess') ||
      this.viewOnlyDAUser()
    );
  }

  viewOnlyDAUser(): boolean {
    const isDepartmentAdmin = this.authService.hasPermission(
      'DepartmentAdminGlobalAccess',
    );
    const daStatusUpdateAllowed = this.isDAStatusUpdateAllowed();
    return isDepartmentAdmin && !daStatusUpdateAllowed;
  }

  private isDAStatusUpdateAllowed(): boolean {
    const currentStatusCode = this.persistedStatus;

    //* SO & RD - formally OSRS Statuses, but DA must retain/regain DA status control
    const daControlledOsrsStatusExceptions = ['SO', 'RD'];
    if (daControlledOsrsStatusExceptions.includes(currentStatusCode)) {
      return true;
    }

    // use the StatusType classification
    const currentStatus = this.activeStatusList.find(
      (status) => status.statusCode === currentStatusCode,
    );

    // unknown/missing status; no Status update access for DA User Role
    if (!currentStatus) {
      return false;
    }
    return currentStatus.statusType === 'DA Status';
  }

  validateGrantWorkTag() {
    if (this.form.controls.grantWorkTag.value) {
      this.grantWorkTagEntered.emit(this.form.controls.grantWorkTag.value);
    }
  }

  copyToSupplierContractId() {
    this.poCoRequestIdEntered.emit(this.form.controls.poCoDocId.value);
  }

  clearPreviousAssignedGA() {
    this.form.controls.previousAssignedGA.patchValue('');
    this.form.controls.previousAssignedGAFirstName.patchValue('');
    this.form.controls.previousAssignedGALastName.patchValue('');
  }

  // Status workflow permissions for DA
  private applyStatusControlAccess(): void {
    const statusControl = this.form?.controls?.status;

    if (!statusControl) {
      return;
    }

    // 'SPA View Only' - Always read-only regardless of the current status
    if (this.authService.hasPermission('ReadOnlyGlobalAccess')) {
      statusControl.disable({ emitEvent: false });
      return;
    }

    /* Department Admin:
     * Enabled when:
     *   - Current persisted status is a DA Status
     *   - SO (Submitted to OSRS) - special exception
     *   - RD (Returned to DA)    - special exception
     * Disabled for all other OSRS statuses.*/
    if (this.authService.hasPermission('DepartmentAdminGlobalAccess')) {
      if (this.isDAStatusUpdateAllowed()) {
        statusControl.enable({ emitEvent: false });
      } else {
        statusControl.disable({ emitEvent: false });
      }
    }
  }
}
